import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  QUERY_PROGRESS_NOT_STARTED,
  SEARCH_ACTION_UPDATE_SOCIAL_WORKER,
} from "core/consts";
import {
  GetNameOptions,
  convertSocialWorkers,
  getAccountValue,
} from "core/model/accounts";
import { findSearchAction } from "core/model/auctions";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { Account, Auction, Patient as PatientType } from "core/types";
import { SelectOption } from "ds_legacy/components/Select/types";
import Spinner from "ds_legacy/components/Spinner";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import useSearchAction from "dsl/hooks/useSearchAction";
import SocialWorkerChange, {
  differentAssignee,
} from "dsl/molecules/SocialWorkerChange";
import { UserIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useMatch } from "react-router-dom";
import { useTranslations } from "translations";
import SocialWorkerAutoComplete from "./SocialWorkerAutocomplete";

const assigneeLayoutClasses =
  "mx-4 flex max-h-full min-w-[280px] flex-row items-center";

export type PatientAssigneeProps = {
  auction: Auction;
  patient: PatientType;
};

export function PatientAssignee({ auction, patient }: PatientAssigneeProps) {
  const isAssessment = useMatch(
    "app/clinic/dashboard/patient/:patient_id/auction/:auction_id/assessing/*",
  );
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const nameOptions: GetNameOptions = {
    withAcademicTitle: false,
    withSalutation: false,
  };
  const translations = useTranslations();
  const getOntology = useGetOntology();
  const { account, socialWorkers } = useSenderAccountContext();
  const assignee = auction.patient.social_worker;

  const currentValue = useMemo(() => {
    return getAccountValue(assignee, getOntology, nameOptions);
  }, [assignee, nameOptions]);

  const assigneeRef = useRef<Account | undefined>(assignee);

  const options = useMemo(
    () =>
      convertSocialWorkers(socialWorkers, getOntology, nameOptions).reduce<
        SelectOption[]
      >((acc, { name, value }) => {
        if (name && value) {
          return [...acc, { id: value, label: name, value }];
        }
        return acc;
      }, []),
    [socialWorkers, translations, nameOptions],
  );

  const canUpdateSocialWorker = findSearchAction(
    auction,
    SEARCH_ACTION_UPDATE_SOCIAL_WORKER,
  );

  const [updateSocialWorker, updateSocialWorkerProgress, resetProgress] =
    useSearchAction({
      actionType: SEARCH_ACTION_UPDATE_SOCIAL_WORKER,
    });

  const onChange = useCallback(
    (newValue: SelectOption) => {
      const social_worker = socialWorkers?.find(({ id }) => id === newValue.id);
      if (social_worker != null) {
        updateSocialWorker({
          auction,
          context: { social_worker_id: social_worker.id },
          onCompleted: (updatedAuction) => {
            const oldAssignee = assigneeRef?.current;
            const [shouldNotify] = differentAssignee(
              updatedAuction?.patient ?? patient,
              { ...patient, social_worker: oldAssignee },
              account,
            );

            if (shouldNotify) setOpen(true);
            else resetProgress();

            if (assigneeRef?.current != null) assigneeRef.current = assignee;
          },
          onError: () =>
            toast({
              color: "danger",
              message: translations.auctionRequest.uploadError,
            }),
        });
      }
    },
    [updateSocialWorker, socialWorkers],
  );

  if (socialWorkers === null)
    return (
      <div className={assigneeLayoutClasses} data-testid="patient-assignee">
        <Spinner id="assignee-options" size="small" />
      </div>
    );

  if (isAssessment) return null;

  return (
    <>
      <div className={assigneeLayoutClasses} data-testid="patient-assignee">
        <UserIcon className="mr-[4px] shrink-0 text-gray-600" size={16} />
        <span className="mb-px font-sans text-base leading-6 whitespace-nowrap text-gray-900">
          {translations.patient.responsiblePerson}
          {translations.general.colon}
          &nbsp;
        </span>
        <SocialWorkerAutoComplete
          currentValue={currentValue}
          defaultValue={options.find(({ id }) => id === currentValue)}
          disabled={
            updateSocialWorkerProgress !== QUERY_PROGRESS_NOT_STARTED ||
            socialWorkers === null ||
            !canUpdateSocialWorker
          }
          options={options}
          onChange={onChange}
        />
      </div>
      {open && assignee != null && (
        <SocialWorkerChange
          assigned={assignee}
          nextNotification={() => {
            setOpen(false);
            resetProgress();
          }}
          patientId={patient?.id}
        />
      )}
    </>
  );
}
